import { Box, Button, Icon, Typography, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AnalyticsIcon from '@mui/icons-material/BarChart';
import AddIcon from '@mui/icons-material/Add';
import  { Trans }  from '@lingui/react/macro';
import { getCurrentLocale } from '../i18n';
import LanguageSwitcher from './LanguageSwitcher';
import LocalizedLink from './LocalizedLink';

// Define interface for navigation items
interface NavItem {
  path: string;
  label: React.ReactNode;
  icon: React.ReactNode;
  isExternal?: boolean;
}

const SimpleNavigation = () => {
  const theme = useTheme();
  const location = useLocation();

  // Get current locale
  const currentLocale = getCurrentLocale();

  // Function to check if a link is active, considering localized routes
  const isActiveLink = (path: string): boolean => {
    // Get the current path without locale prefix
    const currentPath = location.pathname;
    const currentPathWithoutLocale = currentPath.replace(/^\/(en|pt-BR)\//, '/');

    // Get the path without locale prefix
    const pathWithoutLocale = path.replace(/^\/(en|pt-BR)\//, '/');

    // Special case for root path
    if (path === '/') {
      return currentPath === '/' || currentPath === `/${currentLocale}`;
    }

    // For other paths, compare the paths without locale prefixes
    return currentPathWithoutLocale === pathWithoutLocale;
  };

  // Navigation items with simple paths - LocalizedLink will handle the locale prefixes
  const navItems: NavItem[] = [
    {
      path: '/',
      label: <Trans id="nav.servers">Servers</Trans>,
      icon: <DashboardIcon />
    },
    {
      path: '/analytics',
      label: <Trans id="nav.analytics">Global Analytics</Trans>,
      icon: <AnalyticsIcon />
    },
    {
      path: 'https://otarchive.com/manage',
      label: <Trans id="nav.addServer">Add Server</Trans>,
      icon: <AddIcon />,
      isExternal: true
    }
  ];

  return (
    <Box
      sx={{
        width: '100%',
        padding: 2,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderBottom: `1px solid ${theme.palette.primary.main}`,
        boxShadow: `0 0 5px ${(theme as any).custom.glowPrimary}`,
        mb: 3
      }}
    >
      {/* Desktop layout */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          width: '100%'
        }}
      >
        {/* Left side: Logo/Title */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            minWidth: '180px'
          }}
        >
          <Icon
            sx={{
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              fontSize: '2rem',
              marginTop: '1px'
            }}
          >
            <svg viewBox="1020 1400 3610 2330" className="size-6">
              <path d="M1240 3625v-105h-220V1610h220v-210h840v210h1700v430h850v410h-210v430h-210v419l-107 3-108 3-3 108-3 107h-209v210H1240v-105zm2520-240v-105h210v-430h210v-420h210v-160l-1138 2-1137 3-3 108-3 107h-209v420h-210v430h-104c-118 0-116-2-116 98v52h2290v-105zm-2346-121c3-9 6-105 6-215v-199h210v-420h210v-430h1700v-150H1840v-210l-182 2-183 3-3 103-3 102h-209v1430h74c58 0 75-3 80-16z" fill="#000000"></path>
              <path d="M1442 3345c1-27 6-35 20-35s18 8 18 35c0 28-4 35-20 35s-19-6-18-35zm-212-780v-745h209l3-102 3-103 213-3 212-2v210h1700v210H1870v211c0 202 1 211 20 216 11 3 20 12 20 19 0 8-9 14-20 14s-20-7-20-15c0-12-18-15-105-15h-105v420h30c23 0 30 4 30 20 0 15-7 20-29 20-20 0-30-6-34-20-5-18-14-20-106-20h-101v199c0 110-3 206-6 215-5 13-25 16-110 16h-104v-745z" fill="#013f3f"></path>
              <path d="m1442 3418 3-103 108-3 107-3v-208c0-242-13-221 128-221h82v-420h209l3-107 3-108h2337v210l-106 3-106 3v419h-210v430h-210v210H1438l4-102z" fill="#00CCCC"></path>
            </svg>
          </Icon>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 'bold',
              letterSpacing: '1px',
              textShadow: `0 0 3px ${(theme as any).custom.glowPrimary}`,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Trans id="app.title">OTS_Analytics</Trans>
          </Typography>
        </Box>

        {/* Center: Navigation Links */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            ml: 4
          }}
        >
          {navItems.map((item) => (
            <Button
              key={item.path}
              component={item.isExternal ? 'a' : LocalizedLink}
              {...(item.isExternal
                ? { href: item.path, target: "_blank", rel: "noopener noreferrer" }
                : { to: item.path }
              )}
              startIcon={item.icon}
              variant={!item.isExternal && isActiveLink(item.path) ? "contained" : "outlined"}
              sx={{
                borderRadius: 0,
                border: `1px solid ${theme.palette.primary.main}`,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: 'rgba(0, 204, 204, 0.1)',
                  boxShadow: `0 0 5px ${(theme as any).custom.glowPrimary}`,
                },
                ...(!item.isExternal && isActiveLink(item.path) && {
                  backgroundColor: 'rgba(0, 204, 204, 0.2)',
                  boxShadow: `0 0 8px ${(theme as any).custom.glowPrimary}`,
                })
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>

        {/* Right side: Language Switcher */}
        <Box sx={{ marginLeft: 'auto' }}>
          <LanguageSwitcher />
        </Box>
      </Box>

      {/* Mobile layout */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          width: '100%'
        }}
      >
        {/* Top row: Logo and Language Switcher */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          mb: 2
        }}>
          {/* Logo/Title */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon
              sx={{
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                fontSize: '2rem',
                marginTop: '1px'
              }}
            >
              <svg viewBox="1020 1400 3610 2330" className="size-6">
                <path d="M1240 3625v-105h-220V1610h220v-210h840v210h1700v430h850v410h-210v430h-210v419l-107 3-108 3-3 108-3 107h-209v210H1240v-105zm2520-240v-105h210v-430h210v-420h210v-160l-1138 2-1137 3-3 108-3 107h-209v420h-210v430h-104c-118 0-116-2-116 98v52h2290v-105zm-2346-121c3-9 6-105 6-215v-199h210v-420h210v-430h1700v-150H1840v-210l-182 2-183 3-3 103-3 102h-209v1430h74c58 0 75-3 80-16z" fill="#000000"></path>
                <path d="M1442 3345c1-27 6-35 20-35s18 8 18 35c0 28-4 35-20 35s-19-6-18-35zm-212-780v-745h209l3-102 3-103 213-3 212-2v210h1700v210H1870v211c0 202 1 211 20 216 11 3 20 12 20 19 0 8-9 14-20 14s-20-7-20-15c0-12-18-15-105-15h-105v420h30c23 0 30 4 30 20 0 15-7 20-29 20-20 0-30-6-34-20-5-18-14-20-106-20h-101v199c0 110-3 206-6 215-5 13-25 16-110 16h-104v-745z" fill="#013f3f"></path>
                <path d="m1442 3418 3-103 108-3 107-3v-208c0-242-13-221 128-221h82v-420h209l3-107 3-108h2337v210l-106 3-106 3v419h-210v430h-210v210H1438l4-102z" fill="#00CCCC"></path>
              </svg>
            </Icon>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 'bold',
                letterSpacing: '1px',
                textShadow: `0 0 3px ${(theme as any).custom.glowPrimary}`,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Trans id="app.title">OTS_Analytics</Trans>
            </Typography>
          </Box>

          {/* Language Switcher */}
          <LanguageSwitcher />
        </Box>

        {/* Bottom row: Navigation Links */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            justifyContent: 'flex-start'
          }}
        >
          {navItems.map((item) => (
            <Button
              key={item.path}
              component={item.isExternal ? 'a' : LocalizedLink}
              {...(item.isExternal
                ? { href: item.path, target: "_blank", rel: "noopener noreferrer" }
                : { to: item.path }
              )}
              startIcon={item.icon}
              variant={!item.isExternal && isActiveLink(item.path) ? "contained" : "outlined"}
              sx={{
                borderRadius: 0,
                border: `1px solid ${theme.palette.primary.main}`,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: 'rgba(0, 204, 204, 0.1)',
                  boxShadow: `0 0 5px ${(theme as any).custom.glowPrimary}`,
                },
                ...(!item.isExternal && isActiveLink(item.path) && {
                  backgroundColor: 'rgba(0, 204, 204, 0.2)',
                  boxShadow: `0 0 8px ${(theme as any).custom.glowPrimary}`,
                })
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default SimpleNavigation;
